/**
 * The single owner of the /curriculum URL shape. Everything that links into the
 * curriculum tree builds its href here, so the route layout under
 * app/curriculum/[subjectId]/[chapterId]/[unitId]/[lessonId] is described once.
 *
 * The ids are passed by name rather than by position: they are all bare numbers,
 * so a positional signature lets a caller swap chapterId and unitId with nothing
 * to catch it.
 */
export type CurriculumId = number | string;

export type UnitPathIds = {
  subjectId: CurriculumId;
  chapterId: CurriculumId;
  unitId: CurriculumId;
};

export type LessonPathIds = UnitPathIds & { lessonId: CurriculumId };

export function unitPath({ subjectId, chapterId, unitId }: UnitPathIds): string {
  return `/curriculum/${subjectId}/${chapterId}/${unitId}`;
}

export function lessonPath(ids: LessonPathIds): string {
  return `${unitPath(ids)}/${ids.lessonId}`;
}

/**
 * Deep links into a lesson carry either a `content` (LessonContent id) or a
 * `version` param, never both - `content` wins. Returns "" when neither is set.
 */
export function lessonContentQuery(contentParam?: string, versionParam?: string): string {
  if (contentParam) return `?content=${encodeURIComponent(contentParam)}`;
  if (versionParam) return `?version=${encodeURIComponent(versionParam)}`;
  return "";
}

/** A lesson href with its content/version deep-link params preserved. */
export function lessonHref(
  ids: LessonPathIds,
  params?: { contentParam?: string; versionParam?: string },
): string {
  return `${lessonPath(ids)}${lessonContentQuery(params?.contentParam, params?.versionParam)}`;
}

/** The flashcards sub-page of a lesson, scoped to a specific generated content. */
export function flashcardsPath(ids: LessonPathIds, lessonContentId: CurriculumId): string {
  return `${lessonPath(ids)}/flashcards?content=${encodeURIComponent(String(lessonContentId))}`;
}
